/**
 * Messages Page - In-app messaging between Studios and Candidates
 * Similar to Facebook/Instagram private messaging
 */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Send, MessageSquare, User, Clock, Briefcase, Loader2, Check, CheckCheck
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_BACKEND_URL || "";

export default function MessagesPage() {
  const { threadId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newConversation, setNewConversation] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const token = localStorage.getItem("token");
  const toUserId = searchParams.get("to");
  const jobId = searchParams.get("job");
  const quickReply = searchParams.get("quick_reply");
  const quickReplySentRef = useRef(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchThreads();
    fetchUnreadCount();

    // If starting a new conversation, fetch recipient info
    if (toUserId && !threadId) {
      fetchNewRecipient(toUserId);
    }

    // Poll for new messages every 5 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
      if (threadId) {
        fetchMessages(threadId, true);
      } else {
        fetchThreads(true);
      }
    }, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (threadId) {
      setNewConversation(null);
      fetchMessages(threadId);
    }
  }, [threadId]);

  // Auto-send quick reply when landing on a thread with ?quick_reply=... query param
  useEffect(() => {
    if (!quickReply || !threadId || !selectedThread || quickReplySentRef.current) return;
    const canned = {
      interested: "I'm interested! Let's talk more about this job.",
      unavailable: "Thanks for reaching out — unfortunately I'm not available for this one.",
    }[quickReply];
    if (!canned) return;
    quickReplySentRef.current = true;
    (async () => {
      try {
        const recipientId = selectedThread?.other_user?.user_id;
        if (!recipientId) return;
        const url = `${API}/api/messages/send?recipient_id=${recipientId}&content=${encodeURIComponent(canned)}`;
        const res = await fetch(url, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          toast.success(quickReply === "interested" ? "Reply sent — you're marked as interested." : "Reply sent.");
          fetchMessages(threadId, true);
        } else if (res.status === 403) {
          toast.error("Please verify your email to send messages, then try replying manually.");
        } else {
          toast.error("Could not send quick reply — please try replying manually.");
        }
      } catch (err) {
        console.error("Quick reply failed:", err);
        toast.error("Could not send quick reply — please try replying manually.");
      } finally {
        // Always strip the query param so the user isn't stuck with a persistent ?quick_reply URL
        navigate(`/messages/${threadId}`, { replace: true });
      }
    })();
  }, [quickReply, threadId, selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchNewRecipient = async (userId) => {
    try {
      const res = await fetch(`${API}/api/users/profile/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNewConversation({
          other_user: {
            user_id: data.user_id,
            name: data.name,
            profile_image: data.profile_image,
            location: data.location?.city || data.assistant_profile?.city
          }
        });
        setSelectedThread(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Failed to fetch recipient:", error);
    }
  };

  // Stop polling and redirect to login when the session is invalid.
  const handleAuthFailure = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  const fetchThreads = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API}/api/messages/threads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) {
        handleAuthFailure();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setThreads(data.threads || []);
      }
    } catch (error) {
      console.error("Failed to fetch threads:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMessages = async (id, silent = false) => {
    try {
      const res = await fetch(`${API}/api/messages/threads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) {
        handleAuthFailure();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setSelectedThread(data);
        setMessages(data.messages || []);
        // Update unread count after reading
        fetchUnreadCount();
        fetchThreads(true);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) {
        handleAuthFailure();
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Determine recipient - either from existing thread or new conversation
    const recipientId = selectedThread?.other_user?.user_id || newConversation?.other_user?.user_id;
    if (!recipientId) return;

    setSending(true);
    try {
      let url = `${API}/api/messages/send?recipient_id=${recipientId}&content=${encodeURIComponent(newMessage.trim())}`;
      
      // Add job_id if starting from a job context
      if (jobId && !threadId) {
        url += `&job_id=${jobId}`;
      }

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewMessage("");
        
        // If this was a new conversation, navigate to the thread
        if (!threadId && data.thread_id) {
          navigate(`/messages/${data.thread_id}`, { replace: true });
        } else {
          fetchMessages(threadId, true);
        }
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <NavBar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-white/50" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 text-sm">
            Dashboard
          </Button>
        </Link>
        <NavActions showMessages={false} />
      </NavBar>
      
      <main className="flex-1 max-w-6xl mx-auto px-4 pt-20 pb-8">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/dashboard">
            <Button
              variant="ghost"
              size="icon"
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold">Messages</h1>
          {unreadCount > 0 && (
            <span className="bg-coral text-white text-xs px-2 py-0.5 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
          {/* Thread List */}
          <Card className="bg-white/[0.03] border-white/10 overflow-hidden flex flex-col" data-testid="thread-list">
            <div className="p-4 border-b border-white/10">
              <h2 className="font-semibold text-white/70">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {threads.length === 0 ? (
                <div className="p-6 text-center text-white/40">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No conversations yet</p>
                  <p className="text-sm mt-1">Messages from job inquiries will appear here</p>
                </div>
              ) : (
                threads.map((thread) => (
                  <Link
                    key={thread.thread_id}
                    to={`/messages/${thread.thread_id}`}
                    className={`flex items-start gap-3 p-4 border-b border-white/5 hover:bg-white/[0.05] transition-colors ${
                      threadId === thread.thread_id ? "bg-white/[0.08]" : ""
                    }`}
                    data-testid={`thread-${thread.thread_id}`}
                  >
                    {/* Avatar */}
                    {thread.other_user.profile_image ? (
                      <img
                        src={thread.other_user.profile_image}
                        alt={thread.other_user.name}
                        className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-white/40" />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-medium truncate ${thread.unread_count > 0 ? "text-white" : "text-white/70"}`}>
                          {thread.other_user.name}
                        </span>
                        <span className="text-xs text-white/40 flex-shrink-0">
                          {formatTime(thread.last_message_at)}
                        </span>
                      </div>
                      
                      {thread.job && (
                        <div className="flex items-center gap-1 text-xs text-coral mt-0.5">
                          <Briefcase className="w-3 h-3" />
                          <span className="truncate">{thread.job.title}</span>
                        </div>
                      )}
                      
                      {thread.last_message && (
                        <p className={`text-sm truncate mt-1 ${thread.unread_count > 0 ? "text-white/70" : "text-white/40"}`}>
                          {thread.last_message.is_mine && <span className="text-white/30">You: </span>}
                          {thread.last_message.content}
                        </p>
                      )}
                    </div>
                    
                    {thread.unread_count > 0 && (
                      <span className="bg-coral text-white text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0">
                        {thread.unread_count}
                      </span>
                    )}
                  </Link>
                ))
              )}
            </div>
          </Card>

          {/* Message View */}
          <Card className="bg-white/[0.03] border-white/10 overflow-hidden flex flex-col md:col-span-2" data-testid="message-view">
            {!threadId && !newConversation ? (
              <div className="flex-1 flex items-center justify-center text-white/40">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Select a conversation</p>
                  <p className="text-sm mt-1">Or start one from a candidate's profile</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <Link to={`/profile/${(selectedThread || newConversation)?.other_user?.user_id}`}>
                    {(selectedThread || newConversation)?.other_user?.profile_image ? (
                      <img
                        src={(selectedThread || newConversation).other_user.profile_image}
                        alt={(selectedThread || newConversation).other_user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-white/40" />
                      </div>
                    )}
                  </Link>
                  <div className="flex-1">
                    <Link to={`/profile/${(selectedThread || newConversation)?.other_user?.user_id}`} className="font-medium hover:text-coral transition-colors">
                      {(selectedThread || newConversation)?.other_user?.name}
                    </Link>
                    {(selectedThread || newConversation)?.other_user?.location && (
                      <p className="text-xs text-white/40">
                        {typeof (selectedThread || newConversation).other_user.location === 'string' 
                          ? (selectedThread || newConversation).other_user.location
                          : (selectedThread || newConversation).other_user.location?.city 
                            ? `${(selectedThread || newConversation).other_user.location.city}${(selectedThread || newConversation).other_user.location.state ? `, ${(selectedThread || newConversation).other_user.location.state}` : ''}`
                            : null}
                      </p>
                    )}
                  </div>
                  {newConversation && (
                    <span className="text-xs text-coral bg-coral/10 px-2 py-1 rounded">New conversation</span>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 && newConversation && (
                    <div className="text-center text-white/40 py-8">
                      <p>Start the conversation by sending a message</p>
                    </div>
                  )}
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.is_mine ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                          msg.is_mine
                            ? "bg-coral text-white rounded-br-sm"
                            : "bg-white/10 text-white rounded-bl-sm"
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${msg.is_mine ? "text-white/70" : "text-white/40"}`}>
                          <span className="text-[10px]">
                            {formatTime(msg.created_at)}
                          </span>
                          {msg.is_mine && (
                            msg.read ? (
                              <CheckCheck className="w-3 h-3" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-white/[0.05] border-white/10 focus:border-coral text-white"
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="bg-coral hover:bg-coral/90 text-white px-4"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
